import * as React from 'react'
import clx from 'classnames'

import { VehicleUsageIcon } from '@/app/features/icons/VehicleUsage'
import { Card } from '@/app/features/common/Card'
import { VehicleUsageBox } from './VehicleUsageBox'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { SkeletonBox } from '@/app/features/common/SkeletonBox'
import { Loader } from '@/ui'
import { DashboardData } from '../types'

type Props = {
  vehicleUsage?: DashboardData['vehicleUsage'] | null
  isLoading?: boolean
  isRefreshing?: boolean
}

const VehicleUsageSkeleton = () => (
  <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-between tw-gap-6 tw-px-3 tw-pb-3 tw-pt-1">
    <div className="tw-flex tw-w-full tw-flex-1 tw-gap-4">
      {[0, 1].map((column) => (
        <div key={column} className="tw-flex tw-flex-1 tw-flex-col tw-gap-2">
          <SkeletonBox className="tw-h-3 tw-w-24" />
          <SkeletonBox className="tw-h-1 tw-w-full tw-rounded-none" />
          <SkeletonBox className="tw-h-3 tw-w-20" />
          <SkeletonBox className="tw-h-4 tw-w-12" />
        </div>
      ))}
    </div>
    <SkeletonBox className="tw-h-[204px] tw-w-[204px] tw-rounded-full" />
  </div>
)

export const VehicleUsageCard = ({
  vehicleUsage,
  isLoading,
  isRefreshing,
}: Props) => {
  const { t } = useLocalize()
  const minUsagePercent = Number(vehicleUsage?.minUsage?.percent ?? 0)
  const maxUsagePercent = Number(vehicleUsage?.maxUsage?.percent ?? 0)
  const totalUsagePercent = Number(vehicleUsage?.totalUsagePercent ?? 0)

  const hasVehicleUsageBreakdown = minUsagePercent > 0 || maxUsagePercent > 0

  return (
    <Card
      title={t('dashboard.vehicle-driving-time')}
      classNameContent="tw-h-full"
      className={clx(
        'tw-relative tw-h-full tw-w-full !tw-rounded-none tw-border-b-0 tw-border-l-0 tw-border-r tw-bg-primary-linear sm:tw-border-l sm:tw-border-t-0 lg:tw-border-r-0',
      )}
    >
      {isRefreshing && (
        <Loader
          position="absolute"
          className="tw-inset-0 tw-z-10 tw-bg-base-100/60 tw-backdrop-blur-[1px]"
          size="sm"
        />
      )}
      {isLoading ? (
        <VehicleUsageSkeleton />
      ) : (
        <div
          className={clx(
            'tw-flex tw-h-full tw-flex-col tw-items-center tw-gap-6 tw-px-3 tw-pb-3 tw-pt-1',
            {
              'tw-justify-center': !hasVehicleUsageBreakdown,
              'tw-justify-between': hasVehicleUsageBreakdown,
            },
          )}
        >
          <div className="tw-flex tw-w-full tw-flex-1 tw-gap-4">
            <VehicleUsageBox
              title={t('dashboard.min-usage')}
              registrationNumber={
                hasVehicleUsageBreakdown && vehicleUsage
                  ? vehicleUsage.minUsage?.registrationNumber
                  : 'brak danych'
              }
              percent={minUsagePercent || 0}
              className="tw-flex-1"
            />
            <VehicleUsageBox
              title={t('dashboard.max-usage')}
              registrationNumber={
                hasVehicleUsageBreakdown && vehicleUsage
                  ? vehicleUsage.maxUsage?.registrationNumber
                  : 'brak danych'
              }
              percent={maxUsagePercent || 0}
              className="tw-flex-1"
            />
          </div>
          <div className="border tw-flex tw-h-[204px] tw-w-[204px] tw-flex-col tw-items-center tw-justify-between tw-rounded-full tw-border-base-300 tw-bg-base-100 tw-px-6 tw-py-8">
            <div>
              <p className="tw-mb-0 tw-text-center tw-text-2xl tw-font-bold">
                {totalUsagePercent}%
              </p>
              <p className="tw-mb-0 tw-max-w-28 tw-text-center tw-text-xs">
                {t('dashboard.total-vehicle-usage')}
              </p>
            </div>

            <VehicleUsageIcon
              fill={totalUsagePercent > 0 ? undefined : '#D1E2F6'}
              percent={totalUsagePercent}
            />
          </div>
        </div>
      )}
    </Card>
  )
}
